import { useState, useRef, useLayoutEffect } from 'react';
import { FormField } from '@/components/FormField';
import { EditableSelect } from '@/components/ui/editable-select';
import { EditableTextarea, type MarkdownMode } from '@/components/ui/editable-textarea';
import { Button } from '@/components/ui/button';
import { CharacterCount } from '@/components/ui/character-count';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import { ASSESSMENT_VALIDATION, Decision } from '@promo/shared';
import type { SelectOption } from '@/components/ui/editable-select';

interface AssessmentFormProps {
  /** Initial decision for existing assessment */
  initialDecision?: Decision;
  /** Initial justification for existing assessment */
  initialJustification?: string;
  /** Called when user saves the decision/justification */
  onSubmit: (data: { decision: Decision; justification?: string; assessorId?: string }) => void;
  /** Called when user cancels editing */
  onCancel: () => void;
  /** Called when user confirms deletion */
  onDelete?: () => void;
  /** Disable interactions while submitting */
  isSubmitting?: boolean;
  /** Disable editing, show read-only view */
  readOnly?: boolean;
  /** Disable editing decision field, show read-only view */
  decisionReadOnly?: boolean;
  /** Manager override props */
  isManager?: boolean;
  /** Available assessors for manager override */
  availableAssessors?: SelectOption[];
  /** Selected assessor ID for manager override */
  selectedAssessorId?: string;
  /** Callback for assessor selection */
  onAssessorChange?: (assessorId: string) => void;
}

export function AssessmentForm({
  initialDecision,
  initialJustification = '',
  onSubmit,
  onCancel,
  onDelete,
  isSubmitting = false,
  readOnly = false,
  decisionReadOnly = false,
  isManager = false,
  availableAssessors = [],
  selectedAssessorId = '',
  onAssessorChange,
}: AssessmentFormProps) {
  const [decision, setDecision] = useState<Decision | undefined>(initialDecision);
  const [justification, setJustification] = useState(initialJustification);
  const [justificationMarkdownMode, setJustificationMarkdownMode] = useState<MarkdownMode>('edit');
  const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
  const [focusTarget, setFocusTarget] = useState<'decision' | 'justification' | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const decisionRef = useRef<HTMLButtonElement | null>(null);
  const justificationRef = useRef<HTMLTextAreaElement | null>(null);

  const decisionOptions = [
    { value: Decision.PROMOTE, label: 'Promote' },
    { value: Decision.DEFER, label: 'Defer' },
  ];

  useLayoutEffect(() => {
    if (readOnly) {
      setIsInEditMode(false);
      setFocusTarget(null);
    }
  }, [readOnly]);

  useLayoutEffect(() => {
    if (isInEditMode && !readOnly && focusTarget) {
      const timerId = setTimeout(() => {
        if (focusTarget === 'justification' && justificationRef.current) {
          justificationRef.current.focus();
        } else if (focusTarget === 'decision' && decisionRef.current && !decisionReadOnly) {
          decisionRef.current.focus();
          decisionRef.current.click();
        }
      }, 0);
      return () => clearTimeout(timerId);
    }
  }, [isInEditMode, focusTarget, readOnly, decisionReadOnly]);

  const handleEnterEditMode = (target: 'decision' | 'justification') => {
    if (readOnly) return;
    if (target === 'decision' && decisionReadOnly) return;

    if (!isInEditMode) {
      setIsInEditMode(true);
      setFocusTarget(target);
      // Reset markdown mode to edit when entering edit mode for justification
      if (target === 'justification') {
        setJustificationMarkdownMode('edit');
      }
    } else if (focusTarget !== target) {
      setFocusTarget(target); // Switch focus target if already in edit mode
      // Reset markdown mode to edit when switching to justification
      if (target === 'justification') {
        setJustificationMarkdownMode('edit');
      }
    }
  };

  const handleSave = () => {
    if (!decision) return;
    if (decision === Decision.DEFER && justification.trim().length === 0) return;
    if (justification.length > ASSESSMENT_VALIDATION.MAX_JUSTIFICATION_LENGTH) return;

    // Only include assessorId if it's a manager override with a valid selection
    const submitData: { decision: Decision; justification?: string; assessorId?: string } = {
      decision,
      justification,
    };

    if (isManager && selectedAssessorId) {
      submitData.assessorId = selectedAssessorId;
    }

    onSubmit(submitData);
    setIsInEditMode(false);
    setFocusTarget(null);
  };

  const handleActualCancel = () => {
    setDecision(initialDecision);
    setJustification(initialJustification);
    setIsInEditMode(false);
    setFocusTarget(null);
    setShowCancelConfirm(false);
    onCancel();
  };

  const handleCancelClick = () => {
    if ((decision !== initialDecision || justification !== initialJustification) && isInEditMode) {
      setShowCancelConfirm(true);
    } else {
      handleActualCancel();
    }
  };

  const handleDiscard = () => {
    handleActualCancel();
  };

  const effectiveDecisionFieldReadOnly = readOnly || decisionReadOnly || !isInEditMode;
  const effectiveJustificationFieldReadOnly = readOnly || !isInEditMode;

  return (
    <div className="space-y-4" data-testid="assessment-form">
      {/* Manager override: assessor selection */}
      {isManager && availableAssessors.length > 0 && (
        <FormField label="Assessor">
          <EditableSelect
            options={availableAssessors}
            value={selectedAssessorId}
            onValueChange={(val) => onAssessorChange?.(val)}
            placeholder="Select assessor"
            disabled={isSubmitting}
          />
        </FormField>
      )}

      <FormField label="Decision">
        <div className="relative">
          {!isInEditMode && !readOnly && !decisionReadOnly && (
            <div
              onClick={() => handleEnterEditMode('decision')}
              className="absolute inset-0 z-10 cursor-pointer"
              aria-label="Edit decision"
            />
          )}
          <EditableSelect
            ref={decisionRef}
            options={decisionOptions}
            value={decision}
            onValueChange={(val) => {
              setDecision(val as Decision);
              if (!isInEditMode && !readOnly && !decisionReadOnly) {
                handleEnterEditMode('decision');
              } else if (isInEditMode && !readOnly && !decisionReadOnly) {
                // If already in edit mode, ensure focus logic runs if user directly interacts
                setFocusTarget('decision');
              }
            }}
            disabled={isSubmitting || effectiveDecisionFieldReadOnly}
            placeholder="Select decision"
          />
        </div>
      </FormField>

      {decision === Decision.DEFER && (
        <FormField label="Justification">
          <div className="relative">
            {!isInEditMode && !readOnly && (
              <div
                onClick={() => handleEnterEditMode('justification')}
                className="absolute inset-0 z-10 cursor-pointer"
                aria-label="Edit justification"
              />
            )}
            <EditableTextarea
              ref={justificationRef}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              markdown={true}
              showModeToggle={isInEditMode && !readOnly}
              markdownMode={justificationMarkdownMode}
              onModeChange={setJustificationMarkdownMode}
              readOnly={effectiveJustificationFieldReadOnly}
              disabled={isSubmitting || effectiveJustificationFieldReadOnly}
              placeholder="Provide justification for defer"
              maxLength={ASSESSMENT_VALIDATION.MAX_JUSTIFICATION_LENGTH}
              rows={4}
              onClick={() => {
                if (!readOnly && !isInEditMode) {
                  handleEnterEditMode('justification');
                } else if (isInEditMode && !readOnly) {
                  setFocusTarget('justification');
                }
              }}
            />
            {isInEditMode && !readOnly && (
              <CharacterCount
                current={justification.length}
                max={ASSESSMENT_VALIDATION.MAX_JUSTIFICATION_LENGTH}
              />
            )}
          </div>
        </FormField>
      )}

      {isInEditMode && !readOnly && (
        <div className="flex justify-end gap-2 mt-3">
          {onDelete && (
            <Button
              variant="ghost"
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting}
              aria-label="Delete assessment"
              className="mr-auto" // Keep delete on the left
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            type="button"
            onClick={handleCancelClick} // Changed from handleCancel
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              !decision ||
              (decision === Decision.DEFER && justification.trim().length === 0) ||
              justification.length > ASSESSMENT_VALIDATION.MAX_JUSTIFICATION_LENGTH ||
              isSubmitting
            }
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}

      {onDelete && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete assessment?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this assessment? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDelete?.();
                  setShowDeleteConfirm(false);
                }}
                disabled={isSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {showCancelConfirm && (
        <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes in this assessment. Discard them?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting}>Continue Editing</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDiscard}
                disabled={isSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
